import React from 'react';
import { GenericPaginatedDocument } from '@/components/print/GenericPaginatedDocument';
import { buildSimplePrintDocument } from '@/print-engine/adapters/simple-document';

function money(n: unknown): string {
  const v = Number(n ?? 0);
  return Number.isFinite(v)
    ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
}

function days(n: unknown): string {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v.toFixed(2) : '—';
}

export type LeaveSettlementPrintData = {
  settlement_number?: string;
  employee_name?: string;
  employee_code?: string;
  leave_application_number?: string;
  leave_type?: string;
  leave_year?: number;
  from_date?: string;
  to_date?: string;
  balance_available?: number;
  balance_opening?: number;
  balance_accrued?: number;
  balance_carried?: number;
  balance_consumed?: number;
  balance_pending?: number;
  balance_encashed?: number;
  leave_days?: number;
  eligible_days?: number;
  daily_rate?: number;
  leave_amount?: number;
  other_amount?: number;
  net_amount?: number;
  settlement_mode?: string | null;
  target_period_year?: number | null;
  target_period_month?: number | null;
  status?: string;
  notes?: string | null;
  company_name?: string;
};

export const PrintableLeaveSettlement: React.FC<{
  data: LeaveSettlementPrintData;
  companyName?: string;
  onClose?: () => void;
}> = ({ data, companyName, onClose }) => {
  const period =
    data.target_period_year && data.target_period_month
      ? `${String(data.target_period_month).padStart(2, '0')}/${data.target_period_year}`
      : '—';

  const model = buildSimplePrintDocument({
    docType: 'HR_LEAVE_SETTLEMENT',
    title: 'Leave Settlement',
    subtitle: data.leave_type || 'Annual Leave',
    documentNumber: data.settlement_number || '—',
    partyName: data.employee_name || 'Employee',
    templateId: 'a4-portrait',
    letterhead: false,
    metaLines: [
      { label: 'Company', value: companyName || data.company_name || '—' },
      { label: 'Employee', value: data.employee_name || '—' },
      { label: 'Code', value: data.employee_code || '—' },
      { label: 'Leave app', value: data.leave_application_number || '—' },
      { label: 'Leave type', value: data.leave_type || '—' },
      { label: 'Leave year', value: String(data.leave_year || '—') },
      { label: 'Period', value: `${data.from_date || '—'} → ${data.to_date || '—'}` },
      { label: 'Status', value: data.status || '—' },
      { label: 'Settlement mode', value: data.settlement_mode || 'Not set' },
      { label: 'Payroll period', value: period },
    ],
    columns: [
      { key: 'sno', label: '#', width: '40px' },
      { key: 'desc', label: 'Particulars', width: '55%' },
      { key: 'amt', label: 'Value', width: '25%', align: 'right' },
    ],
    lines: [
      { lineNumber: 1, description: 'Opening balance', cells: { amt: days(data.balance_opening) } },
      { lineNumber: 2, description: 'Accrued', cells: { amt: days(data.balance_accrued) } },
      { lineNumber: 3, description: 'Carried forward', cells: { amt: days(data.balance_carried) } },
      { lineNumber: 4, description: 'Consumed', cells: { amt: days(data.balance_consumed) } },
      { lineNumber: 5, description: 'Pending', cells: { amt: days(data.balance_pending) } },
      { lineNumber: 6, description: 'Encashed', cells: { amt: days(data.balance_encashed) } },
      { lineNumber: 7, description: 'Available (snapshot)', cells: { amt: days(data.balance_available) } },
      { lineNumber: 8, description: 'Leave days (application)', cells: { amt: days(data.leave_days) } },
      { lineNumber: 9, description: 'Eligible days', cells: { amt: days(data.eligible_days) } },
      { lineNumber: 10, description: 'Daily rate', cells: { amt: money(data.daily_rate) } },
      { lineNumber: 11, description: 'Leave amount', cells: { amt: money(data.leave_amount) } },
      { lineNumber: 12, description: 'Other amount (ticket / allowance)', cells: { amt: money(data.other_amount) } },
    ],
    finals: [{ id: 'totals', heightPx: 56, keepTogetherWithNext: true }],
    renderPayload: {
      totals: [{ label: 'Net settlement amount', value: money(data.net_amount) }],
      notes: data.notes || undefined,
    },
  });

  return <GenericPaginatedDocument model={model} onClose={onClose} />;
};

export default PrintableLeaveSettlement;
